import React, { useState } from 'react'
import { EmployeeTree } from '@/api/workforce'
import { Card } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import {
  ChevronDown,
  ChevronRight,
  UserCircle,
  Mail,
  Briefcase,
  Building2
} from 'lucide-react'

interface HierarchyTreeViewProps {
  managementTree: EmployeeTree
  onEmployeeClick: (employee: EmployeeTree) => void
}

interface EmployeeNodeProps {
  employee: EmployeeTree
  onEmployeeClick: (employee: EmployeeTree) => void
  level?: number
}

function EmployeeNode({ employee, onEmployeeClick, level = 0 }: EmployeeNodeProps) {
  const [isExpanded, setIsExpanded] = useState(level === 0)
  const hasReports = !!(employee.reports && employee.reports.length > 0)

  const name = employee.personalInfo?.name || employee.email || 'Desconhecido'
  const nameParts = name.split(' ')
  const initials =
    nameParts.length >= 2
      ? `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`
      : `${nameParts[0][0]}${nameParts[0][1] || ''}`

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'bg-success/20 text-success border-success/40'
      case 'INACTIVE':
        return 'bg-brand-gray/20 text-brand-gray border-brand-gray/40'
      case 'TERMINATED':
        return 'bg-error/20 text-error border-error/40'
      default:
        return 'bg-brand-gray/20 text-brand-gray border-brand-gray/40'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'ativo'
      case 'INACTIVE':
        return 'inativo'
      case 'TERMINATED':
        return 'demitido'
      case 'PENDING':
        return 'pendente'
      default:
        return 'ativo'
    }
  }

  return (
    <div className="relative">
      {/* Employee Card */}
      <Card
        className={`
          relative transition-all duration-300 cursor-pointer group
          hover:border-brand-primary hover:shadow-monumenta-brand
          ${level === 0 ? 'border-brand-primary shadow-monumenta-brand' : ''}
        `}
        onClick={() => onEmployeeClick(employee)}
      >
        <div className="p-4">
          <div className="flex items-center space-x-3">
            {/* Avatar */}
            <div className="w-12 h-12 bg-gradient-to-br from-brand-primary to-brand-accent-600 rounded-full flex items-center justify-center shadow-monumenta group-hover:shadow-monumenta-brand transition-all duration-300 flex-shrink-0">
              <span className="text-sm font-bold text-brand-dark font-display">
                {initials.toUpperCase()}
              </span>
            </div>

            {/* Employee Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-semibold text-white truncate group-hover:text-brand-primary transition-colors duration-200">
                {name}
              </h3>
              <p className="text-sm text-brand-gray truncate font-body">
                {employee.employment?.role?.name || 'Função não definida'}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={getStatusColor(employee.employment?.status || 'ACTIVE')}>
                  <span className="text-xs">
                    {getStatusLabel(employee.employment?.status || 'ACTIVE')}
                  </span>
                </Badge>
                {employee.employment?.department?.name && (
                  <span className="text-xs text-brand-gray flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    {employee.employment.department.name}
                  </span>
                )}
              </div>
            </div>

            {/* Expand/Collapse Button */}
            {hasReports && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  setIsExpanded(!isExpanded)
                }}
                className="btn-icon hover:bg-brand-primary/10 text-brand-gray hover:text-brand-primary flex-shrink-0"
              >
                {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </button>
            )}
          </div>

          {/* Quick Info on Hover */}
          <div className="mt-2 text-xs text-brand-gray opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <div className="flex items-center gap-1">
              <Mail className="w-3 h-3" />
              <span className="truncate">{employee.email}</span>
            </div>
          </div>
        </div>

        {/* Direct Reports Count Badge */}
        {hasReports && employee.reports && (
          <div className="absolute -top-2 -right-2 bg-brand-primary text-brand-dark text-xs rounded-full w-6 h-6 flex items-center justify-center font-display font-medium shadow-monumenta-brand">
            {employee.reports.length}
          </div>
        )}
      </Card>

      {/* Direct Reports */}
      {hasReports && isExpanded && employee.reports && (
        <div className="ml-8 mt-4 space-y-4 relative">
          {/* Connection Line */}
          <div className="absolute left-6 top-0 bottom-0 w-px bg-brand-gray/40"></div>

          {employee.reports.map((report, index) => (
            <div key={report.employeeId} className="relative">
              {/* Horizontal connection */}
              <div className="absolute -left-8 top-6 w-8 h-px bg-brand-gray/40"></div>

              <EmployeeNode
                employee={report}
                onEmployeeClick={onEmployeeClick}
                level={level + 1}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export function HierarchyTreeView({
  managementTree,
  onEmployeeClick
}: HierarchyTreeViewProps) {
  if (!managementTree) {
    return (
      <Card className="p-12 text-center">
        <UserCircle className="w-16 h-16 mx-auto mb-4 text-brand-gray" />
        <h3 className="font-display text-lg font-semibold text-white mb-2">
          Nenhum Dado de Hierarquia
        </h3>
        <p className="text-brand-gray font-body">
          Não foi possível carregar a árvore hierárquica.
        </p>
      </Card>
    )
  }

  return (
    <Card className="p-6">
      <div className="mb-4">
        <h2 className="font-display text-xl font-bold text-white mb-2">
          Árvore Hierárquica
        </h2>
        <p className="text-brand-gray text-sm font-body">
          Clique nos funcionários para ver detalhes. Use as setas para expandir/recolher.
        </p>
      </div>

      <div className="overflow-auto" style={{ minHeight: '400px' }}>
        <EmployeeNode employee={managementTree} onEmployeeClick={onEmployeeClick} />
      </div>
    </Card>
  )
}
